<template>
  <div>
    <base-nav
      v-model="showMenu"
      class="navbar-absolute top-navbar"
      type="white"
      :transparent="true"
      :style="themeBorderStyle"
    >
      <div class="topbar-container">
        <!-- Left: Cabo Logo -->
        <router-link to="/" class="topbar-logo d-none d-lg-flex">
          <cabo-logo size="30" />
          <span class="logo-text">cabo.party</span>
        </router-link>

        <!-- Desktop Nav Items -->
        <nav class="topbar-nav d-none d-lg-flex">
          <a
            v-for="item in navItems"
            :key="item.label"
            :href="item.href"
            class="nav-link"
            :style="themeColor"
          >
            {{ item.label }}
          </a>
        </nav>

        <!-- Right: CTA Buttons (Desktop) & Sidebar Toggle (Mobile) -->
        <div class="topbar-actions">
          <button
            class="btn btn-book-table d-none d-lg-block"
            @click="talkWithUs"
          >
            <img v-lazy="astroImg" height="20px" width="20px" alt="" />
            TALK WITH A HUMAN
          </button>

          <button
            class="btn btn-book-table d-none d-lg-block"
            @click="bookTable"
          >
            <span v-html="$icons.crown" style="display: inline-block"></span>
            BOOK A TABLE
          </button>

          <button
            v-if="pageConfig"
            class="btn btn-config d-none d-lg-block"
            @click="showConfigPanel = !showConfigPanel"
            :class="{ active: showConfigPanel }"
          >
            ⚙ Config
          </button>

          <sidebar-toggle-button />
        </div>
      </div>

      <!-- Config Panel -->
      <transition name="config-slide">
        <div
          v-if="showConfigPanel && pageConfig"
          class="config-panel d-none d-lg-block"
        >
          <div class="config-panel-inner">
            <div class="config-row" v-if="pageConfig.slug">
              <span class="config-label">Page</span>
              <span class="config-value">{{ pageConfig.slug }}</span>
            </div>
            <div class="config-row" v-if="pageConfig.metadata">
              <span class="config-label">Colors</span>
              <span class="config-value config-colors">
                <span
                  class="color-swatch"
                  :style="{
                    backgroundColor:
                      pageConfig.metadata.primary_color || '#333',
                  }"
                  :title="pageConfig.metadata.primary_color"
                ></span>
                <code>{{ pageConfig.metadata.primary_color || '—' }}</code>
                <span
                  class="color-swatch"
                  :style="{
                    backgroundColor:
                      pageConfig.metadata.secondary_color || '#333',
                  }"
                  :title="pageConfig.metadata.secondary_color"
                ></span>
                <code>{{ pageConfig.metadata.secondary_color || '—' }}</code>
              </span>
            </div>
            <div class="config-row" v-if="pageConfig.metadata">
              <span class="config-label">Links</span>
              <span class="config-value">
                {{ (pageConfig.metadata.smart_links || []).length }} smart ·
                {{ (pageConfig.metadata.generic_links || []).length }} generic
              </span>
            </div>
            <div class="config-row" v-if="pageConfig.metadata">
              <span class="config-label">Carousels</span>
              <span class="config-value">{{
                (pageConfig.metadata.carousels || []).length
              }}</span>
            </div>
            <div class="config-row" v-if="pageConfig.id">
              <a
                :href="`https://app.cosmicjs.com/tulumparty-cabo/objects/${pageConfig.id}`"
                target="_blank"
                rel="noopener noreferrer"
                class="config-cosmic-link"
              >
                Open in Cosmic →
              </a>
            </div>
          </div>
        </div>
      </transition>
    </base-nav>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { BaseNav } from '@/components';
import SidebarToggleButton from './SidebarToggleButton';
import CaboLogo from '@/components/CaboLogo';
import { getContrastColor } from '@/common/colorUtils';
import { getNavItemsForRoute } from '@/common/CityNavConfig';

export default {
  name: 'DashboardNavbar',
  components: {
    SidebarToggleButton,
    BaseNav,
    CaboLogo,
  },
  data() {
    return {
      activeNotifications: false,
      logo: 'https://imgix.cosmicjs.com/3acdf350-9b87-11ee-b34c-99d1d89e42eb-tp-logo-40pt.png',
      showMenu: false,
      showConfigPanel: false,
      searchModalVisible: false,
      astroImg:
        'https://imgix.cosmicjs.com/1098dee0-e8ea-11ef-b333-e101bec29f3d-atro-transparent.png',
      searchQuery: '',
      themeSecondaryColor: null,
      themePrimaryColor: '#efefef',
    };
  },
  computed: {
    routeName() {
      const { name } = this.$route;
      return this.capitalizeFirstLetter(name || '');
    },
    isRTL() {
      return this.$rtl ? this.$rtl.isRTL : false;
    },
    themeColor() {
      if (this.themePrimaryColor && !this.isTooDark(this.themePrimaryColor)) {
        return `color: ${this.themePrimaryColor} !important;`;
      }
      return 'color: #efefef !important;';
    },
    themeTextColor() {
      if (this.themeSecondaryColor) {
        return getContrastColor(this.themeSecondaryColor);
      }
      return '#ffffff';
    },
    themeBorderStyle() {
      if (
        this.themeSecondaryColor &&
        !this.isTooDark(this.themeSecondaryColor)
      ) {
        return `border-bottom: 2px solid ${this.themeSecondaryColor} !important;`;
      }
      return 'border-bottom: 2px solid rgba(223, 183, 132, 0.4) !important;';
    },
    navItems() {
      return getNavItemsForRoute(this.$route ? this.$route.path : '');
    },
    ...mapState({
      pageConfig: (state) => state.page_config,
    }),
  },
  watch: {
    $route() {
      this.showConfigPanel = false;
      this.$store.commit('CLEAR_PAGE_CONFIG');
    },
  },
  mounted() {
    this.$root.$on('navbar-theme-change', this.onNavbarThemeChange);
  },
  beforeDestroy() {
    this.$root.$off('navbar-theme-change', this.onNavbarThemeChange);
  },
  methods: {
    isTooDark(color) {
      if (!color) return true;
      if (color.startsWith('#')) {
        const hex = color.replace('#', '');
        if (hex.length === 6) {
          const r = parseInt(hex.substr(0, 2), 16);
          const g = parseInt(hex.substr(2, 2), 16);
          const b = parseInt(hex.substr(4, 2), 16);
          const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
          return luminance < 0.45;
        }
      } else if (color.startsWith('rgb')) {
        const match = color.match(/\d+/g);
        if (match && match.length >= 3) {
          const r = parseInt(match[0], 10);
          const g = parseInt(match[1], 10);
          const b = parseInt(match[2], 10);
          const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
          return luminance < 0.45;
        }
      }
      return false;
    },
    onNavbarThemeChange(theme) {
      if (!theme) return;
      this.themeSecondaryColor = theme.secondaryColor;
      this.themePrimaryColor = theme.primaryColor;

      const root = document.documentElement;
      if (theme && theme.isActive) {
        if (theme.primaryColor && !this.isTooDark(theme.primaryColor)) {
          root.style.setProperty('--navbar-link-color', theme.primaryColor);
        } else {
          root.style.setProperty('--navbar-link-color', '#efefef');
        }
        if (theme.secondaryColor) {
          root.style.setProperty('--navbar-hover-color', theme.secondaryColor);
        }
      } else {
        root.style.removeProperty('--navbar-link-color');
        root.style.removeProperty('--navbar-hover-color');
      }
    },
    capitalizeFirstLetter(string) {
      if (!string) return '';
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    closeDropDown() {
      this.activeNotifications = false;
    },
    toggleSidebar() {
      if (this.$sidebar) {
        this.$sidebar.displaySidebar(!this.$sidebar.showSidebar);
      }
    },
    hideSidebar() {
      if (this.$sidebar) {
        this.$sidebar.displaySidebar(false);
      }
    },
    toggleMenu() {
      this.showMenu = !this.showMenu;
    },
    bookTable() {
      const url =
        'https://api.whatsapp.com/send/?phone=524421177251&text=Hi%21+I%27d+like+to+inquire+about+table+reservations+in+Los+Cabos.&type=phone_number&app_absent=0';
      window.open(url);
      if (this.$rollbar) {
        this.$rollbar.info('Book A Table Cabo');
      }
    },
    talkWithUs() {
      const url =
        'https://wa.me/524421177251?text=Hey%21%20I%27m%20a%20real%20human%20at%20cabo.party%2C%20ready%20to%20help.%20How%20Can%20I%20Help%20%3F\n';
      window.open(url);
      if (this.$rollbar) {
        this.$rollbar.info('Talk With Us Cabo');
      }
    },
    getContrastColor,
  },
};
</script>

<style scoped>
.top-navbar {
  top: 0px;
  background: rgba(10, 10, 10, 0.85) !important;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  position: fixed;
  z-index: 1000;
  transition: all 0.3s ease;
}

.topbar-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.8rem 2rem;
}

.topbar-logo {
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
  cursor: pointer;
}

.topbar-logo:hover {
  text-decoration: none;
}

.topbar-logo .logo-text {
  font-weight: 700;
  font-size: 1.1rem;
  color: #fff;
  text-decoration: none;
  letter-spacing: 1.5px;
  font-family: 'Poppins', sans-serif;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.3);
}

.topbar-nav {
  display: flex;
  gap: 2.5rem;
  align-items: center;
}

.nav-link {
  color: var(--navbar-link-color, #efefef);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.3s ease;
  font-family: 'Poppins', sans-serif;
  position: relative;
  padding-bottom: 5px;
}

.nav-link::after {
  content: '';
  position: absolute;
  width: 0;
  height: 2px;
  bottom: 0;
  left: 50%;
  background: var(
    --navbar-hover-color,
    linear-gradient(90deg, #dfb784 0%, #db9454 100%)
  );
  transition: all 0.3s ease;
  transform: translateX(-50%);
}

.nav-link:hover {
  color: var(--navbar-hover-color, #dfb784);
  text-shadow: 0 0 8px rgba(223, 183, 132, 0.5);
}

.nav-link:hover::after {
  width: 100%;
}

.topbar-actions {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.btn-book-table {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #fff;
  padding: 0.6rem 1.2rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Poppins', sans-serif;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  gap: 8px;
  backdrop-filter: blur(5px);
}

.btn-book-table:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

/* Reduce navbar size by 15% for screens <= 1500px */
@media (max-width: 1500px) {
  .topbar-container {
    padding: 0.68rem 1.7rem;
  }

  .topbar-nav {
    gap: 2.125rem;
  }

  .nav-link {
    font-size: 0.7225rem;
    letter-spacing: 0.85px;
  }

  .topbar-actions {
    gap: 1.275rem;
  }

  .btn-book-table {
    padding: 0.51rem 1.02rem;
    font-size: 0.68rem;
    gap: 6.8px;
  }

  .btn-book-table img {
    height: 17px;
    width: 17px;
  }
}

/* Admin Config Panel */
.btn-config {
  background: rgba(255, 200, 50, 0.1);
  border: 1px solid rgba(255, 200, 50, 0.4);
  color: #ffc832;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: 'Poppins', sans-serif;
}

.btn-config:hover,
.btn-config.active {
  background: rgba(255, 200, 50, 0.25);
  border-color: #ffc832;
}

.config-panel {
  width: 100%;
  background: rgba(15, 15, 15, 0.95);
  backdrop-filter: blur(12px);
  border-top: 1px solid rgba(255, 200, 50, 0.2);
  overflow: hidden;
}

.config-slide-enter-active,
.config-slide-leave-active {
  transition: max-height 0.3s ease, opacity 0.3s ease;
  max-height: 200px;
}

.config-slide-enter,
.config-slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.config-panel-inner {
  display: flex;
  align-items: center;
  gap: 30px;
  padding: 10px 2rem;
  flex-wrap: wrap;
}

.config-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Poppins', sans-serif;
  font-size: 0.78rem;
}

.config-label {
  color: #888;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
  font-size: 0.7rem;
}

.config-value {
  color: #ddd;
}

.config-value code {
  background: rgba(255, 255, 255, 0.08);
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.72rem;
  color: #ccc;
}

.config-colors {
  display: flex;
  align-items: center;
  gap: 6px;
}

.color-swatch {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: inline-block;
}

.config-cosmic-link {
  color: #ffc832;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.78rem;
  transition: opacity 0.2s;
}

.config-cosmic-link:hover {
  opacity: 0.8;
  text-decoration: underline;
}
</style>
